import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../../job.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Job from '../../../../../models/job.js';
import type Person from '../../../../../models/person.js';
import definition from '@job-schemas/public-ledger/auto-payments/setup.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { nsFormFieldStyles } from '../../../../../ns-styles/index.js';
import { nsStripeServiceContext } from '../../../../../ns-services/contexts.js';
import { NSStripeService } from '../../../../../ns-services/ns-stripe-service.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import { claimJob, isUnclaimed } from '../../../../../helpers/job-claim.js';
import { lambdaErrorCode } from '../../../../../helpers/lambda.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';
import { personDisplayName } from '../../../../../helpers/person.js';
import { formatCredits } from '../../../../../helpers/public-ledger-credits.js';
import { formatUnitPence } from '../../../../../helpers/credit-pricing.js';
import {
  AUTOPAY_CODES,
  COMPANY_CARD_NOTICE,
  MANDATE_TEXT,
  MANDATE_TEXT_VERSION,
  RATE_NOTE,
  cardText,
  setupIntentFromSearch,
  setupStage,
  setupActions,
  type SetupAction,
  type AutoPaymentRow,
  type SetupFigures,
} from '../../../../../helpers/auto-payments.js';
import { AutopaySettingsForm } from './settings-form.js';
import {
  autopayFigures,
  autopaySetupIntent,
  confirmAutopaySetup,
  proposeAutopaySettings,
} from '../../../../../helpers/auto-payments-api.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

const logger = createLogger('ns-public-ledger-auto-payments-setup');

/**
 * Set up automatic payments (#25 phase 3): claimable by the company's public ledger signatories. The signatory who claims
 * it saves a card for payments made while they're not present (a SetupIntent; they become the card's owner, and only they
 * can propose with it), then chooses the company account's threshold (credits) and target (£), the operating account's
 * threshold and target (credits), the two monthly limits (£) and whether to be told when prices move. Each amount shows
 * its conversion at today's price with a note that the price changes. Propose checks the settings again on the server and
 * records them; the card is then completed with `proposed`, and the workflow asks every signatory to sign them. Nothing is
 * charged in this phase.
 */
@customElement(NSPublicLedgerAutoPaymentsSetup.is)
export default class NSPublicLedgerAutoPaymentsSetup extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-auto-payments-setup';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-setup'],
      icon: 'bank-card',
    });
  }

  static styles = [
    nsFormFieldStyles,
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .fields {
        display: grid;
        gap: var(--wa-space-m);
      }

      .field input {
        max-width: 16rem;
      }

      wa-radio-group::part(form-control-label) {
        font-size: var(--wa-font-size-m);
        font-weight: 600;
      }
    `,
  ];

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  @state()
  private __row: AutoPaymentRow | null = null;

  @state()
  private __figures: SetupFigures | null = null;

  // The amounts form, shared with Change automatic payments amounts (#25 phase 6)
  private __form = new AutopaySettingsForm(this, { figures: () => this.__figures, busy: () => this.__busy });

  @state()
  private __clientSecret: string | null = null;

  // The transaction propose answered, so the card knows its own proposal before the row's update arrives
  @state()
  private __proposedTransactionId: string | null = null;

  @state()
  private __busy = false;

  @state()
  private __people: Person[] = [];

  async connectedCallback() {
    await super.connectedCallback();
    const job = this._job;
    if (!job) return;
    await this.__loadRow();
    if (job.status !== 'COMPLETE' && !isUnclaimed(job)) {
      await this.__returnFromBank();
      await this.__loadFigures();
    }

    this._subscriptions.subscribe(`job.${job.id}.*`, () => {
      const updated = this._db?.get(`job.${job.id}`) as Job | undefined;
      if (updated) {
        this._job = updated;
        this.requestUpdate();
      }
    });
    this._subscriptions.subscribe('plAutoPayment.*', () => {
      void this.__loadRow();
    });
  }

  disconnectedCallback() {
    this.__stripe?.cleanElements();
    super.disconnectedCallback();
  }

  private get __me() {
    return this._directory?.me?.id ?? null;
  }

  private get __stage() {
    return setupStage(this.__row, this.__me, this.__proposedTransactionId);
  }

  private async __loadRow() {
    const companyId = this._job?.companyId;
    if (!companyId || !this._db) return;
    try {
      const found = await this._db.query('plAutoPayment', { companyId: { $eq: companyId } });
      const [row] = (found?.results ?? []) as unknown as AutoPaymentRow[];
      this.__row = row ?? null;
      const ownerId = this.__row?.pendingChange?.proposed?.card?.cardOwnerId;
      if (ownerId && !this.__people.some((p) => p.id === ownerId)) {
        const person = (await this._db.getById('people', ownerId)) as Person | undefined;
        if (person) this.__people = [...this.__people, person];
      }
    } catch (err) {
      logger.warn('Unable to read the automatic payments settings', err);
    }
  }

  private async __loadFigures() {
    const companyId = this._job?.companyId;
    if (!companyId || !this._auth?.token) return;
    try {
      this.__figures = await autopayFigures(this._auth.token, companyId);
    } catch (err) {
      this.showError(lambdaRefusal(err, "Today's price can't be read. Please try again."));
    }
  }

  // After a bank's redirect (3-D Secure), Stripe brings the browser back here naming the SetupIntent: record the card
  private async __returnFromBank() {
    const returned = setupIntentFromSearch(window.location.search);
    if (!returned) return;
    const url = new URL(window.location.href);
    for (const key of ['setup_intent', 'setup_intent_client_secret', 'redirect_status']) url.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', url.toString());
    if (!returned.succeeded) {
      this.showError('Your bank did not confirm the card. Please try again.');
      return;
    }
    await this.__recordCard(returned.setupIntentId);
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this.__me ?? undefined };
      await this.__loadFigures();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  // Mounts the card form for a new SetupIntent on the company's automatic payments customer
  private async __addCard() {
    const job = this._job;
    if (!job || !this._auth?.token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    this.loading('Starting the card form');
    try {
      const { clientSecret } = await autopaySetupIntent(this._auth.token, job.id);
      this.__clientSecret = clientSecret;
      await this.updateComplete;
      // The Stripe service is shared: a form another card left behind would otherwise be reused
      this.__stripe?.cleanElements();
      await this.__stripe?.mountStripeElement(this, clientSecret);
    } catch (err) {
      this.__clientSecret = null;
      this.showError(lambdaRefusal(err, 'The card form could not be started. Please try again.'));
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private async __saveCard() {
    if (!this.__stripe || this.__busy) return;
    this.__busy = true;
    this.clearError();
    this.loading('Saving the card');
    try {
      // null: Stripe refused the card and shows why in the form
      const setupIntentId = await this.__stripe.confirmCardSetup();
      if (setupIntentId) await this.__recordCard(setupIntentId);
    } catch (err) {
      this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private async __recordCard(setupIntentId: string) {
    const job = this._job;
    if (!job || !this._auth?.token) return;
    try {
      await confirmAutopaySetup(this._auth.token, {
        jobId: job.id,
        setupIntentId,
        mandateTextVersion: MANDATE_TEXT_VERSION,
      });
      this.__stripe?.cleanElements();
      this.__clientSecret = null;
      await this.__loadRow();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
    }
  }

  private async __propose() {
    const job = this._job;
    if (!job || !this._auth?.token || this.__busy) return;
    this.clearError();
    const amounts = this.__form.submit();
    if (!amounts) return;

    this.__busy = true;
    this.loading('Proposing the settings');
    try {
      const { transactionId } = await proposeAutopaySettings(this._auth.token, {
        jobId: job.id,
        settings: amounts,
      });
      this.__proposedTransactionId = transactionId;
      await this.__loadRow();
      this.loaded();
      await this.outcome('proposed');
    } catch (err) {
      this.loaded();
      const code = lambdaErrorCode(err);
      const message = lambdaRefusal(err, 'The settings could not be proposed. Please try again.');
      if (code === AUTOPAY_CODES.settingsInvalid) {
        if (!this.__form.showServerProblems(message)) this.showError(message);
        // The server checked at its own price: refresh it so the conversions match
        await this.__loadFigures();
      } else {
        this.showError(message);
        if (code === AUTOPAY_CODES.noRate) await this.__loadFigures();
      }
    } finally {
      this.__busy = false;
    }
  }

  private __nameOf(personId: string) {
    if (personId === this.__me) return 'you';
    const person = this.__people.find((p) => p.id === personId);
    return person ? personDisplayName(person) : 'another signatory';
  }

  private __renderCard() {
    const card = this.__row?.pendingChange?.proposed?.card;
    const other = card?.cardLast4 && card.cardOwnerId && card.cardOwnerId !== this.__me ? card : null;
    return html`
      <div class="job-panel">
        <h3>The card</h3>
        <p>
          Save a card for your company's automatic payments. You'll be its owner: only you can confirm a payment with
          your bank if it asks.
        </p>
        ${other
          ? html`
              <p class="note">
                ${this.__nameOf(other.cardOwnerId as string)} saved the ${cardText(other)} on this set-up. Only they can
                propose the settings with it. Saving your own card replaces it.
              </p>
            `
          : nothing}
        <p><strong>${COMPANY_CARD_NOTICE}</strong></p>
        <p class="note">${MANDATE_TEXT}</p>
      </div>
      ${this.__clientSecret
        ? html`
            <div class="job-panel">
              <slot name="stripe-payment-form"></slot>
            </div>
          `
        : nothing}
    `;
  }

  private __renderAmounts() {
    const card = cardText(this.__row?.pendingChange?.proposed?.card);
    return html`
      <div class="job-panel">
        <h3>The card</h3>
        <p>${card ? `Your ${card} is saved for these payments.` : 'Your card is saved for these payments.'}</p>
        <wa-button
          variant="brand"
          appearance="outlined"
          size="s"
          type="button"
          ?disabled=${this.__busy}
          @click=${() => this.__addCard()}
        >
          Use a different card
        </wa-button>
      </div>
      ${this.__clientSecret
        ? html`
            <div class="job-panel">
              <slot name="stripe-payment-form"></slot>
            </div>
          `
        : nothing}
      ${this.__form.render()}
    `;
  }

  private __renderPrice() {
    const unit = this.__figures?.unitPricePence ?? null;
    return html`
      <div class="primary-info" slot="aside">
        <span class="info-icon" aria-hidden="true">i</span>
        <h3>Today's price</h3>
        <ns-detail-list
          .rows=${[
            { label: 'Price per credit', value: unit !== null ? formatUnitPence(unit) : "Can't be read right now" },
            {
              label: 'Smallest purchase',
              value: this.__figures ? `${formatCredits(this.__figures.minCredits)} credits` : undefined,
            },
          ]}
        ></ns-detail-list>
        <p class="note">${RATE_NOTE}</p>
        <p>
          Once proposed, every signatory signs these settings on the public ledger. nodeStream pays the public ledger
          fee for this signing. Nothing is charged until they have all signed.
        </p>
      </div>
    `;
  }

  private __renderStage() {
    const job = this._job;
    if (!job) return nothing;
    if (job.status === 'COMPLETE') {
      return html`
        <div class="job-panel">
          <p>This set-up is finished.</p>
        </div>
      `;
    }
    if (isUnclaimed(job)) {
      return html`
        <div class="job-panel">
          <p>Claim this job to save a card and choose the amounts. Any public ledger signatory can claim it.</p>
        </div>
      `;
    }
    switch (this.__stage) {
      case 'ALREADY_SET_UP':
        return html`
          <div class="job-panel">
            <p>Automatic payments are already set up for your company. You can cancel this job.</p>
          </div>
        `;
      case 'CHANGE_OPEN':
        return html`
          <div class="job-panel">
            <p>
              Settings are already waiting for the signatories to sign. Once that has finished, you can set up again if
              it's still needed. You can cancel this job.
            </p>
          </div>
        `;
      case 'PROPOSED':
        return html`
          <div class="job-panel" role="status">
            <p>
              The settings are proposed. Complete this job and every signatory will be asked to sign them on the public
              ledger.
            </p>
          </div>
        `;
      case 'AMOUNTS':
        return this.__renderAmounts();
      default:
        return this.__renderCard();
    }
  }

  render() {
    const job = this._job;
    const working = !!job && job.status !== 'COMPLETE' && !isUnclaimed(job);
    return html`
      <ns-job-layout>
        <p slot="intro">
          Automatic payments keep your company's public ledger account topped up with nodeStream credits from a saved
          card, so its public ledger actions aren't held up.
        </p>
        ${this.__renderStage()} ${working ? this.__renderPrice() : nothing}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    const buttons: Record<SetupAction, TemplateResult> = {
      CLAIM: html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `,
      CANCEL: html`
        <wa-button
          variant="brand"
          appearance="outlined"
          type="button"
          ?disabled=${this.__busy}
          @click=${() => this.outcome('cancel')}
        >
          Cancel set-up
        </wa-button>
      `,
      COMPLETE: html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.outcome('proposed')}>
          Complete
        </wa-button>
      `,
      SAVE_CARD: html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__saveCard()}>
          Save card
        </wa-button>
      `,
      ADD_CARD: html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__addCard()}>
          Add card
        </wa-button>
      `,
      PROPOSE: html`
        <wa-button
          variant="brand"
          type="button"
          ?disabled=${this.__busy || !this.__figures?.unitPricePence}
          @click=${() => this.__propose()}
        >
          Propose settings
        </wa-button>
      `,
    };
    const actions = setupActions(this.__stage, { claimed: !isUnclaimed(job), cardFormOpen: !!this.__clientSecret });
    return html`
      ${actions.map((action) => buttons[action])}
    `;
  }
}
